/**
 * One-time "set up a passkey" offer shown right after a new account signs in
 * (ED-2799). A passkey made here is saved to the platform keychain (iCloud
 * Keychain, Google Password Manager, Windows Hello) for the app.eleplan.com
 * relying party, so the same person can later open the Eleplan app on any of
 * their devices and sign in with it, with no password step. On the web the
 * "done" step points at the app stores for that reason.
 */
import React, { useState } from 'react';
import { ActivityIndicator, Linking, Modal, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store';
import { store } from '../store';
import { SET_PASSKEY_PROMPT } from '../store/constants';
import { fetchUser } from '../store/actions/user';
import { registerPasskey } from '../utilities/passkeys';
import { useTheme } from '../hooks/useTheme';
import Icon from './Icon';

const APP_STORE_URL = 'https://apps.apple.com/us/app/eleplan/id6760241583';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.eleplan.app';

function biometricWording(): string {
  if (Platform.OS === 'ios') return 'Face ID or Touch ID';
  if (Platform.OS === 'android') return 'your fingerprint or face';
  return 'Touch ID, Windows Hello or your phone';
}

function keychainWording(): string {
  if (Platform.OS === 'ios') return 'iCloud Keychain';
  if (Platform.OS === 'android') return 'Google Password Manager';
  return 'your browser or password manager';
}

export default function PasskeySetupSheet() {
  const dispatch = useDispatch<any>();
  const theme = useTheme();
  const prompt = useSelector((state: RootState) => state.session.passkeyPrompt);
  const [step, setStep] = useState<'offer' | 'done'>('offer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!prompt?.show) return null;

  const close = () => {
    dispatch({ type: SET_PASSKEY_PROMPT, payload: null });
    setStep('offer');
    setError('');
  };

  const setUp = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await registerPasskey();
      if (result.success) {
        const state = store.getState();
        dispatch(fetchUser(state.authentication?.user?.cognito_id, state.session?.account?.account_id));
        setStep('done');
      } else if (!/cancel/i.test(result.message || '')) {
        setError(result.message || 'Could not create a passkey. You can add one later in Settings.');
      }
    } finally {
      setBusy(false);
    }
  };

  const open = (url: string) => Linking.openURL(url).catch(() => {});
  const isWeb = Platform.OS === 'web';

  return (
    <Modal visible transparent animationType="fade" onRequestClose={close}>
      <View style={s.backdrop}>
        <View style={[s.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <View style={[s.iconWrap, { backgroundColor: `${theme.colors.primary}1A` }]}>
            {step === 'done'
              ? <Icon icon={['fas', 'shield-check']} size={26} color={theme.colors.primary} />
              : <Icon icon={['fad', 'fingerprint']} size={26} color={theme.colors.primary} />}
          </View>

          {step === 'offer' ? (
            <>
              <Text style={[s.title, { color: theme.colors.text }]}>Sign in faster next time</Text>
              <Text style={[s.body, { color: theme.colors.muted }]}>
                Add a passkey so you can sign in with {biometricWording()} instead of a password. It is saved to {keychainWording()} and works everywhere you are signed in, including the Eleplan app on your phone.
              </Text>
              {error ? <Text style={s.error}>{error}</Text> : null}
              <TouchableOpacity
                style={[s.primary, { backgroundColor: theme.colors.primary }]}
                onPress={() => void setUp()}
                disabled={busy}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel="Set up passkey"
              >
                {busy ? <ActivityIndicator color="#fff" size="small" /> : <Text style={s.primaryText}>Set up passkey</Text>}
              </TouchableOpacity>
              <TouchableOpacity style={s.secondary} onPress={close} disabled={busy} accessibilityRole="button" accessibilityLabel="Not now">
                <Text style={[s.secondaryText, { color: theme.colors.muted }]}>Not now</Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <Text style={[s.title, { color: theme.colors.text }]}>You are set</Text>
              <Text style={[s.body, { color: theme.colors.muted }]}>
                Next time, choose "Sign in with a passkey".{isWeb ? ' The Eleplan app signs in with this same passkey, so there is nothing to type on your phone.' : ''}
              </Text>
              {isWeb ? (
                <View style={s.storeRow}>
                  <TouchableOpacity style={[s.store, { borderColor: theme.colors.border }]} onPress={() => open(APP_STORE_URL)} accessibilityRole="link" accessibilityLabel="Get the iPhone app">
                    <Icon icon={['fab', 'apple']} size={16} color={theme.colors.text} />
                    <Text style={[s.storeText, { color: theme.colors.text }]}>iPhone app</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[s.store, { borderColor: theme.colors.border }]} onPress={() => open(PLAY_STORE_URL)} accessibilityRole="link" accessibilityLabel="Get the Android app">
                    <Icon icon={['fab', 'android']} size={16} color={theme.colors.text} />
                    <Text style={[s.storeText, { color: theme.colors.text }]}>Android app</Text>
                  </TouchableOpacity>
                </View>
              ) : null}
              <TouchableOpacity style={[s.primary, { backgroundColor: theme.colors.primary }]} onPress={close} activeOpacity={0.85} accessibilityRole="button" accessibilityLabel="Done">
                <Text style={s.primaryText}>Done</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
}

const s = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: { width: '100%', maxWidth: 420, borderRadius: 16, borderWidth: 1, padding: 24, alignItems: 'center' },
  iconWrap: { width: 56, height: 56, borderRadius: 28, alignItems: 'center', justifyContent: 'center', marginBottom: 14 },
  title: { fontSize: 20, fontWeight: '700', textAlign: 'center', marginBottom: 8 },
  body: { fontSize: 14, lineHeight: 20, textAlign: 'center', marginBottom: 18 },
  error: { fontSize: 13, textAlign: 'center', marginBottom: 12, color: '#e5484d' },
  primary: { alignSelf: 'stretch', paddingVertical: 13, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  primaryText: { color: '#fff', fontSize: 15, fontWeight: '600' },
  secondary: { paddingVertical: 12, alignItems: 'center' },
  secondaryText: { fontSize: 14, fontWeight: '500' },
  storeRow: { flexDirection: 'row', gap: 10, alignSelf: 'stretch', marginBottom: 14 },
  store: { flex: 1, flexDirection: 'row', gap: 8, alignItems: 'center', justifyContent: 'center', paddingVertical: 11, borderRadius: 10, borderWidth: 1 },
  storeText: { fontSize: 14, fontWeight: '500' },
});
